// Minimal promise wrapper around IndexedDB for the offline copy of the cookbook and the outbox.
// One database per server and account, so signing in as someone else never shows their data.

const STORE = 'kv';
let dbName = 'milk';
let dbp: Promise<IDBDatabase> | null = null;

/** Picks the database for this server + person (call before any other use). */
export function useDatabase(name: string) {
  if (name === dbName && dbp) return;
  dbp?.then((db) => db.close()).catch(() => {});
  dbp = null;
  dbName = name;
}

function open(): Promise<IDBDatabase> {
  if (!dbp) {
    dbp = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') return reject(new Error('IndexedDB unavailable'));
      const req = indexedDB.open(dbName, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    dbp.catch(() => { dbp = null; });
  }
  return dbp;
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T> {
  return open().then((db) => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const r = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(r ? (r.result as T) : (undefined as T));
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

export const idb = {
  get: <T>(key: string) => tx<T | undefined>('readonly', (s) => s.get(key) as IDBRequest<T | undefined>).catch(() => undefined),
  set: (key: string, value: unknown) => tx('readwrite', (s) => { s.put(value, key); }).catch(() => undefined),
  del: (key: string) => tx('readwrite', (s) => { s.delete(key); }).catch(() => undefined),
  /** All values whose key starts with `prefix`. */
  getPrefix: <T>(prefix: string) => tx<T[]>('readonly', (s) => s.getAll(IDBKeyRange.bound(prefix, `${prefix}￿`)) as IDBRequest<T[]>).catch(() => [] as T[]),
  /** Several puts and deletes in one transaction. */
  batch: (puts: [string, unknown][], dels: string[] = []) => tx('readwrite', (s) => { for (const [k, v] of puts) s.put(v, k); for (const k of dels) s.delete(k); }).catch(() => undefined),
  clear: () => tx('readwrite', (s) => { s.clear(); }).catch(() => undefined),
  /** Deletes this account's whole database (sign-out). */
  drop: async () => {
    const name = dbName;
    await dbp?.then((db) => db.close()).catch(() => {});
    dbp = null;
    await new Promise<void>((r) => { try { const q = indexedDB.deleteDatabase(name); q.onsuccess = q.onerror = q.onblocked = () => r(); } catch { r(); } });
  },
};
